import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { superAdminAPI } from '../services/api';
import Logo from '../components/Logo';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [email, setEmail] = useState('');
  const [userRoles, setUserRoles] = useState([]);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }

    // Validate token on component mount
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await superAdminAPI.validateResetToken({ token });
      if (response.data.valid) {
        setTokenValid(true);
        // Update email from the response if available
        if (response.data.userEmail) {
          setEmail(response.data.userEmail);
        }
        // Store user roles for redirect logic
        if (response.data.userRoles) {
          setUserRoles(response.data.userRoles);
        }
      } else {
        setTokenValid(false);
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      setTokenValid(false);
    }
  };

  const getRedirectUrl = () => {
    // Resolve portal URLs from runtime env (window.__ENV__) or Vite env, with safe fallbacks
  const runtimeEnv = (typeof window !== 'undefined' && window.__ENV__) ? window.__ENV__ : {};
  const ADMIN_PORTAL_URL = runtimeEnv.ADMIN_PORTAL_URL || import.meta.env.VITE_ADMIN_PORTAL_URL || '';
  const STUDENT_PORTAL_URL = runtimeEnv.STUDENT_PORTAL_URL || import.meta.env.VITE_STUDENT_PORTAL_URL || '';

    const joinUrl = (base, path) => {
      if (!base) return path;
      const b = String(base).replace(/\/+$/, '');
      const p = String(path).replace(/^\/+/, '');
      return `${b}/${p}`;
    };

    // Determine redirect URL based on user roles
    if (userRoles.includes('ROLE_SUPER_ADMIN')) {
      return '/login'; // Super admin portal login
    } else if (userRoles.includes('ROLE_CONTENT_GENERATOR') ||
               userRoles.includes('ROLE_CONTENT_REVIEWER') ||
               userRoles.includes('ROLE_CONTENT_SUPERVISOR')) {
      // Content team users should go to super admin portal login
      return '/login';
    } else if (userRoles.includes('ROLE_ADMIN')) {
      // Admin users should go to admin portal
  return ADMIN_PORTAL_URL ? joinUrl(ADMIN_PORTAL_URL, 'login') : '/login';
    } else if (userRoles.includes('ROLE_STUDENT')) {
      // Student users should go to student portal
  return STUDENT_PORTAL_URL ? joinUrl(STUDENT_PORTAL_URL, 'login') : '/login';
    }
    // Default to super admin portal login
    return '/login';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      await superAdminAPI.resetPassword({
        token,
        newPassword: formData.password
      });

      setSuccess(true);

      // Redirect to appropriate portal after 3 seconds
      setTimeout(() => {
        const redirectUrl = getRedirectUrl();
        if (redirectUrl.startsWith('http')) {
          // External URL - redirect to different portal
          window.location.href = redirectUrl;
        } else {
          // Internal URL - use navigate
          navigate(redirectUrl);
        }
      }, 3000);

    } catch (error) {
      console.error('Password reset failed:', error);
      if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: 'Failed to reset password. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <Logo size="lg" showText={true} />
            <div className="mt-8 bg-red-50 border border-red-200 p-6 rounded-lg">
              <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-red-900 mb-2">Invalid Reset Link</h2>
              <p className="text-red-700 mb-4">
                This password reset link is invalid or has expired. Please request a new password reset.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="btn-primary w-full"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 p-8">
          <div className="text-center">
            <Logo size="lg" showText={true} />
            <div className="mt-8 bg-green-50 border border-green-200 p-6 rounded-lg">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-green-900 mb-2">Password Reset Successful</h2>
              <p className="text-green-700 mb-4">
                Your password has been successfully reset. You will be redirected to the login page shortly.
              </p>
              <button
                onClick={() => {
                  const redirectUrl = getRedirectUrl();
                  if (redirectUrl.startsWith('http')) {
                    window.location.href = redirectUrl;
                  } else {
                    navigate(redirectUrl);
                  }
                }}
                className="btn-primary w-full"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          <Logo size="lg" showText={true} />
          <h2 className="mt-6 text-2xl font-bold text-gray-900">Reset Your Password</h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter your new password for {email}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {errors.general && (
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <p className="text-red-600 text-sm">{errors.general}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`input-field pl-10 pr-10 ${errors.password ? 'border-red-300' : ''}`}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`input-field pl-10 pr-10 ${errors.confirmPassword ? 'border-red-300' : ''}`}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
